import React, { useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  type AuthProvider,
  getMsalConfig,
  getStoredProvider,
  setStoredProvider,
  clearStoredProvider,
} from './msalConfig';

interface Props {
  children: React.ReactNode;
}

export default function AuthProviderWrapper({ children }: Props) {
  const [provider, setProvider] = useState<AuthProvider | null>(getStoredProvider);

  function selectProvider(p: AuthProvider) {
    setStoredProvider(p);
    setProvider(p);
  }

  if (!provider) {
    return <LoginSelection onSelect={selectProvider} />;
  }

  const msalInstance = new PublicClientApplication(getMsalConfig(provider));

  return (
    <MsalProvider instance={msalInstance}>
      <SignOutContext.Provider value={() => { clearStoredProvider(); setProvider(null); }}>
        {children}
      </SignOutContext.Provider>
    </MsalProvider>
  );
}

// Exposed so Layout's Sign Out button can clear the provider selection
export const SignOutContext = React.createContext<() => void>(() => {});

// ─── Login selection screen ───────────────────────────────────────────────────

function LoginSelection({ onSelect }: { onSelect: (p: AuthProvider) => void }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Onyx Financial Coordinator</h1>
          <p className="text-slate-500 text-sm mt-1">BHSD – Special Projects</p>
        </div>
        <p className="text-slate-600 text-sm mb-6 text-center">Select your organization to sign in</p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => onSelect('onyx')}
            className="w-full py-3 px-4 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
          >
            Onyx Staff
          </button>
          <button
            onClick={() => onSelect('bhsd')}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            BHSD Users
          </button>
        </div>
      </div>
    </div>
  );
}
